/**
 * GA4 General Analytics Tracking
 *
 * Automatic tracking for:
 * - Button clicks (xe-button components)
 * - Navigation clicks (xe-nav-item)
 * - External links
 * - Card clicks
 *
 * Usage:
 * 1. Import and initialize in app.js
 * 2. Optionally add data-ga-* attributes for custom tracking
 */

class GA4Tracking {
  constructor() {
    this.initialized = false;
    this.debug = false;
  }

  init() {
    if (this.initialized) return;

    if (customElements.get('xe-button')) {
      this.setupButtonTracking();
      this.setupNavigationTracking();
      this.setupCardTracking();
      this.setupExternalLinkTracking();
      this.initialized = true;

      if (this.debug) {
        console.log('[GA4 Tracking] Initialized');
      }
    } else {
      setTimeout(() => this.init(), 100);
    }
  }

  setupButtonTracking() {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('xe-button');
      if (!button) return;

      const buttonText = button.textContent.trim();
      const buttonHref = button.getAttribute('href');
      const buttonVariant = button.getAttribute('variant') || 'default';
      const section = this.findParentSection(button);

      const isExternal = buttonHref && (
        buttonHref.startsWith('http') ||
        buttonHref.startsWith('//') ||
        button.getAttribute('target') === '_blank'
      );

      const eventName = isExternal ? 'cta_click_external' : 'cta_click';

      this.trackEvent(eventName, {
        cta_text: buttonText,
        cta_url: buttonHref || 'no-url',
        cta_variant: buttonVariant,
        cta_location: section,
        cta_type: 'button'
      });
    });
  }

  setupNavigationTracking() {
    document.addEventListener('click', (event) => {
      const navItem = event.target.closest('xe-nav-item');
      if (!navItem) return;

      const navText = navItem.textContent.trim();
      const navTarget = navItem.getAttribute('href');

      this.trackEvent('navigation_click', {
        nav_text: navText,
        nav_target: navTarget,
        nav_type: 'main-navigation'
      });
    });
  }

  setupCardTracking() {
    document.addEventListener('click', (event) => {
      const card = event.target.closest('xe-card[href], xe-card[interactive]');
      if (!card) return;

      if (event.target.closest('xe-button')) return;

      const cardHref = card.getAttribute('href');
      const cardTitle = card.querySelector('[slot="title"]')?.textContent.trim() || 'Untitled Card';
      const cardVariant = card.getAttribute('variant') || 'default';
      const section = this.findParentSection(card);

      this.trackEvent('card_click', {
        card_title: cardTitle,
        card_url: cardHref || 'no-url',
        card_variant: cardVariant,
        card_location: section
      });
    });
  }

  setupExternalLinkTracking() {
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link) return;

      if (link.closest('xe-button, xe-nav-item, xe-card')) return;

      const linkHref = link.getAttribute('href');
      const isExternal = linkHref && (
        linkHref.startsWith('http') ||
        linkHref.startsWith('//') ||
        link.getAttribute('target') === '_blank'
      );

      if (!isExternal) return;

      const linkText = link.textContent.trim();
      const section = this.findParentSection(link);

      this.trackEvent('outbound_click', {
        link_text: linkText,
        link_url: linkHref,
        link_location: section
      });
    });
  }

  findParentSection(element) {
    const section = element.closest('section[id], [id*="section"]');
    if (section) {
      return section.id || 'unknown-section';
    }
    return 'no-section';
  }

  trackEvent(eventName, params) {
    params.timestamp = new Date().toISOString();

    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, params);

      if (this.debug) {
        console.log(`[GA4 Tracking] ${eventName}:`, params);
      }
    } else {
      if (this.debug) {
        console.warn('[GA4 Tracking] gtag not found - ensure GA4 is installed');
        console.log(`[GA4 Tracking] Would have tracked: ${eventName}`, params);
      }
    }
  }

  track(eventName, params = {}) {
    this.trackEvent(eventName, params);
  }
}

const ga4 = new GA4Tracking();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => ga4.init());
} else {
  ga4.init();
}

export { ga4, GA4Tracking };
